import type { FC, ReactNode } from 'react';
import { Suspense, useMemo } from 'react';
import { Route } from 'react-router';
import { ActionModeProvider } from 'src/components/action-mode';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { useAppShellData } from 'src/components/layout/app-shell/use-app-shell-data';
import { useAppShell } from 'src/components/layout/app-shell/use-app-shell-provider';
import { UserMenu } from 'src/components/layout/header/user-menu';
import {
  LayoutContainer,
  LayoutContent,
  LayoutSidebar,
  Sidebar,
} from 'src/components/layout/sidebar';
import { RootRoutes } from 'src/components/root-routes';
import { PageSpinner } from 'src/components/spinner-container';
import { Sticky } from 'src/components/sticky';
import { Heading } from 'src/components/text/heading';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { Platform } from 'src/pages/platform';
import { AssignmentSettings } from 'src/pages/settings/assignment-settings';
import { AuctionsSettings } from 'src/pages/settings/auctions';
import { DirectFcfsSettings } from 'src/pages/settings/direct-fcfs';
import { GeneralTransportSettings } from 'src/pages/settings/general-transport-settings';
import { HydrogenFcfsSettings } from 'src/pages/settings/hydrogen-fcfs';
import { IntegrationClientAuth } from 'src/pages/settings/integration-client-auth';
import { NotificationSettings } from 'src/pages/settings/notifications';
import { OrganisationManagement } from 'src/pages/settings/organisation';
import { PermissionGroups } from 'src/pages/settings/permission-groups';
import { PremiumServices } from 'src/pages/settings/premium-services';
import { Profile } from 'src/pages/settings/profile';
import { RegularFcfsSettings } from 'src/pages/settings/regular-fcfs';
import { RemitReportingSettings } from 'src/pages/settings/remit-reporting';
import { StandardReportSettings } from 'src/pages/settings/reporting/standard-reports';
import { SurrenderSettings } from 'src/pages/settings/surrender';

export const SettingsLayout: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { contextualSidebarPortal } = useAppShell();
  const appShellData = useAppShellData();
  const isNewDesignActive = useIsNewDesignActive();
  const actionMode = useMemo(() => ({ mode: 'sidebar-item' as const }), []);

  if (!appShellData) return <PageSpinner />;

  const userMenu = (
    <UserMenu
      authorizationService={
        appShellData.authorizationService?.response.data ?? null
      }
      organisationService={
        appShellData.organisationService?.response.data ?? null
      }
      capacityManagementService={appShellData.capacityManagementService ?? null}
      regularFcfsService={appShellData.regularFcfsService}
      directFcfsService={appShellData.directFcfsService}
      hydrogenFcfsRoot={appShellData.hydrogenFcfsService}
      assignmentService={appShellData.assignmentService?.response.data ?? null}
      remitReportingService={appShellData.remitReportingService}
      surrenderServiceRoot={appShellData.surrenderServiceRoot}
      show={{
        personalSettings: true,
        companySettings: true,
        transportSettings: true,
        storageSettings: true,
        shipperSettings: true,
        otherSettings: false,
        reportingSettings: true,
      }}
    />
  );

  if (isNewDesignActive) {
    return (
      <>
        {contextualSidebarPortal.renderContent(
          <div className="grid gap-2xl">
            <Heading mode="title" size="sm">
              Settings
            </Heading>

            <ActionModeProvider value={actionMode}>
              {userMenu}
            </ActionModeProvider>
          </div>
        )}

        {children}
      </>
    );
  }

  return (
    <LayoutContainer>
      <LayoutSidebar>
        <Sticky>
          <Sidebar>{userMenu}</Sidebar>
        </Sticky>
      </LayoutSidebar>

      <LayoutContent>{children}</LayoutContent>
    </LayoutContainer>
  );
};

export const Settings: FC = () => {
  return (
    <SettingsLayout>
      <Suspense fallback={<PageSpinner />}>
        <RootRoutes>
          <Route path="/settings/profile" element={<Profile />} />

          <Route
            path="/settings/integration-client-auth"
            element={<IntegrationClientAuth />}
          />

          <Route
            path="/settings/notifications/*"
            element={<NotificationSettings />}
          />

          <Route
            path="/settings/organisation/*"
            element={<OrganisationManagement />}
          />

          <Route
            path="/settings/permission-groups/*"
            element={<PermissionGroups />}
          />

          <Route
            path="/settings/assignment-settings/*"
            element={<AssignmentSettings />}
          />

          <Route path="/settings/platform" element={<Platform />} />

          <Route
            path="/settings/regular-fcfs/*"
            element={<RegularFcfsSettings />}
          />

          <Route
            path="/settings/direct-fcfs"
            element={<DirectFcfsSettings />}
          />

          <Route
            path="/settings/hydrogen-fcfs"
            element={<HydrogenFcfsSettings />}
          />

          <Route
            path="/settings/premium-services"
            element={<PremiumServices />}
          />

          <Route
            path="/settings/general-transport-settings"
            element={<GeneralTransportSettings />}
          />

          <Route
            path="/settings/reporting/standard-reports"
            element={<StandardReportSettings />}
          />

          <Route path="/settings/auctions" element={<AuctionsSettings />} />

          <Route
            path="/settings/remit-reporting/*"
            element={<RemitReportingSettings />}
          />

          <Route path="/settings/surrender" element={<SurrenderSettings />} />

          <Route path="*" element={<ThrowNotFound />} />
        </RootRoutes>
      </Suspense>
    </SettingsLayout>
  );
};
